// Book Analysis API — learner-stage-specific analysis for the Book Analysis
// page. Backed by GET /api/content/books/:id/analysis?stage=<1-5>, which
// proxies the engine server-side and returns a trimmed page payload.

import { apiClient } from '../apiClient'
import type { FitClassification } from '@/types'
import type { SkillId } from '@/data/skillDictionary'

export interface AnalysisWordEntry {
  word: string
  count: number
  /** Effective decoding ceiling (post-override float) — §1A filter key. */
  stage: number | null
  phonograms: string[]
  skillIds: SkillId[]
  /** DEP-191 B1: one skill per phonogram, INDEX-ALIGNED with `phonograms`
   *  (unlike `skillIds`, which dedups + drops nulls). Used to show the grapheme
   *  of the phonogram whose skill matches a section. Optional for back-compat. */
  phonogramSkillIds?: Array<SkillId | null>
  /** DEP-201: one stage (float) per phonogram, INDEX-ALIGNED with `phonograms`.
   *  Companion to `phonogramSkillIds` — the Decoding-challenge blocks use it to
   *  keep only above-reader patterns, assign each word to its single
   *  highest-stage pattern, and pick that pattern's grapheme. Optional for
   *  back-compat (older payloads fall back to skill-id magnitude). */
  phonogramStages?: Array<number | null>
  /** Zipf frequency — used to avoid sight-frequency example words. */
  zipf: number | null
  inflection?: string | null
  requiresPreTeaching?: boolean
}

export interface AnalysisVocabEntry {
  word: string
  zipf: number
  syllableCount: number
  frequency: number
  storyRelevance: number
  /** 1 = hardest (rarest) word in this book's vocab pool. */
  difficultyRank: number | null
}

export interface ExperienceInsight {
  title: string
  /** Contains the literal {name} placeholder for the learner's name. */
  body: string
}

export interface BookAnalysis {
  stage: number
  stageFloat: number
  learnerStageName: string | null
  /** Ingestion-time LLM insight for this book + fit classification; null
   *  when the book has no stored signals (page omits it). */
  experienceInsight: ExperienceInsight | null
  fit: {
    matchScore: number
    classification: FitClassification
    decodingPercentage: number
    vocabularyAccessibility: number
    vaBreakdown: Record<string, number> | null
    challengeLevel: string | null
    challengeLine: string | null
  }
  wordLists: {
    current: AnalysisWordEntry[]
    frontier: AnalysisWordEntry[]
    above: AnalysisWordEntry[]
  }
  /** Token totals per list (incl. the below list the payload drops) —
   *  §2.3 current_prevalence share (CHANGE 21 / CG-5). Nullable while old
   *  cached payloads drain. */
  wordTotals?: {
    below: number
    current: number
    frontier: number
    above: number
  } | null
  vocabulary: AnalysisVocabEntry[]
}

export async function fetchBookAnalysis(
  bookId: string,
  stage: number,
): Promise<BookAnalysis> {
  const { data } = await apiClient.get<BookAnalysis>(
    `/api/content/books/${bookId}/analysis`,
    { params: { stage } },
  )
  return data
}
